/**
 * Device Sensors Manager
 * Handles all device sensor events (compass, accelerometer, etc.) independently from React
 * Dispatches custom window events for React components to listen to
 * Manages compass calibration entirely outside of React
 */

import { HeadingSmoothing } from './heading-smooth'

class DeviceSensorsManager {
  constructor() {
    this.isCompassAvailable = false
    this.currentHeading = 0
    this.isListening = false
    this.handlers = new Map()

    // Compass calibration state (completely outside React)
    this.isCalibrating = false
    this.calibrationHeadings = []
    this.recentHeadings = []
    this.compassAccuracy = null // Current accuracy in degrees
    this.calibrationDismissed = false // User dismissed calibration - don't re-trigger for a while

    // Heading smoothing
    this.headingSmoothing = new HeadingSmoothing(0.7)
  }

  /**
   * Initialize device sensors and request permissions
   */
  async init() {
    if (this.isListening) return this.isCompassAvailable

    // Check if DeviceOrientationEvent is available
    if (typeof DeviceOrientationEvent === 'undefined') {
      console.warn('DeviceOrientationEvent not supported on this device')
      return false
    }

    try {
      // Request permission for iOS 13+
      if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        const permissionState = await DeviceOrientationEvent.requestPermission()
        if (permissionState !== 'granted') {
          console.warn('DeviceOrientationEvent permission denied')
          return false
        }
      }

      // Start listening to device orientation
      this.attachCompassListener()
      this.isListening = true
      this.isCompassAvailable = true
      console.log('Device compass initialized')
      return true
    } catch (error) {
      console.error('Failed to initialize device sensors:', error)
      // Fallback: try without permission
      this.attachCompassListener()
      this.isListening = true
      this.isCompassAvailable = true
      console.log('Device compass initialized (fallback)')
      return true
    }
  }

  /**
   * Attach the compass listener to window
   */
  attachCompassListener() {
    const handleDeviceOrientation = (event) => {
      let heading = event.webkitCompassHeading || event.alpha

      if (event.webkitCompassHeading === undefined) {
        // Fallback for non-webkit browsers (Android)
        heading = 360 - event.alpha
      }

      // Adjust for screen orientation
      const screenOrientation = window.screen.orientation?.type || 'portrait-primary'
      const orientationAngle = this.getOrientationAngle(screenOrientation)
      heading = (heading + orientationAngle) % 360

      // Apply smoothing
      const smoothedHeading = this.headingSmoothing.update(heading)
      this.currentHeading = smoothedHeading

      // Track recent headings for variance calculation
      this.recentHeadings.push(heading)
      if (this.recentHeadings.length > 10) this.recentHeadings.shift()

      // If calibrating, collect headings and update progress
      if (this.isCalibrating) {
        this.calibrationHeadings.push(heading)
        this.updateCalibrationState()
      } else {
        // Always monitor accuracy even when not calibrating
        this.updateCompassAccuracy()
      }

      // Dispatch custom window event with heading data
      this.dispatchHeadingEvent(smoothedHeading)
    }

    // Store reference for cleanup
    this.handlers.set('deviceorientation', handleDeviceOrientation)

    window.addEventListener('deviceorientation', handleDeviceOrientation, true)
  }

  /**
   * Get orientation angle adjustment based on screen orientation
   */
  getOrientationAngle(screenOrientation) {
    switch (screenOrientation) {
      case 'portrait-primary':
        return 90
      case 'landscape-primary':
        return 180
      case 'portrait-secondary':
        return 270
      case 'landscape-secondary':
        return 0
      default:
        return 0
    }
  }

  /**
   * Calculate compass accuracy from recent heading variance
   */
  calculateHeadingVariance(headings) {
    if (headings.length < 2) return 0

    const mean = headings.reduce((a, b) => a + b, 0) / headings.length
    const variance = headings.reduce((sum, h) => {
      let diff = h - mean
      if (diff > 180) diff -= 360
      if (diff < -180) diff += 360
      return sum + diff * diff
    }, 0) / headings.length

    return Math.sqrt(variance)
  }

  /**
   * Monitor compass accuracy during normal operation
   */
  updateCompassAccuracy() {
    if (this.recentHeadings.length >= 5) {
      this.compassAccuracy = this.calculateHeadingVariance(this.recentHeadings)

      // Trigger calibration if accuracy is poor (> 30°) and not dismissed
      if (this.compassAccuracy > 30 && !this.isCalibrating && !this.calibrationDismissed) {
        this.startCalibration()
      }

      // Dispatch accuracy update event
      this.dispatchAccuracyEvent(this.compassAccuracy)
    }
  }

  /**
   * Start compass calibration
   */
  startCalibration() {
    this.isCalibrating = true
    this.calibrationHeadings = []
    this.dispatchCalibrationEvent({
      state: 'started',
      progress: 0
    })
  }

  /**
   * Update calibration state and check completion
   */
  updateCalibrationState() {
    if (!this.isCalibrating || this.calibrationHeadings.length < 10) return

    const stdDev = this.calculateHeadingVariance(this.calibrationHeadings)

    // Progress: 50% from readings, 50% from accuracy improvement
    const readingsProgress = Math.min(100, (this.calibrationHeadings.length / 50) * 50)
    const accuracyProgress = Math.max(0, Math.min(50, (30 - stdDev) * 1.67))
    const totalProgress = Math.min(100, readingsProgress + accuracyProgress)

    // Dispatch progress update
    this.dispatchCalibrationEvent({
      state: 'in-progress',
      progress: totalProgress,
      readings: this.calibrationHeadings.length,
      accuracy: stdDev
    })

    // Auto-complete when criteria met: 50+ readings AND variance < 15°
    if (this.calibrationHeadings.length >= 50 && stdDev < 15) {
      this.completeCalibration()
    }
  }

  /**
   * Complete calibration
   */
  completeCalibration() {
    this.isCalibrating = false
    this.compassAccuracy = this.calculateHeadingVariance(this.calibrationHeadings)
    this.calibrationHeadings = []
    this.calibrationDismissed = false // Reset flag once calibration is successful

    this.dispatchCalibrationEvent({
      state: 'completed',
      progress: 100,
      accuracy: this.compassAccuracy
    })
  }

  /**
   * Cancel calibration
   */
  cancelCalibration() {
    this.isCalibrating = false
    this.calibrationHeadings = []
    this.calibrationDismissed = true // User dismissed - don't auto-trigger again
    this.dispatchCalibrationEvent({
      state: 'cancelled'
    })
  }

  /**
   * Dispatch custom window event with heading data
   */
  dispatchHeadingEvent(heading) {
    const event = new CustomEvent('deviceheading', {
      detail: { heading }
    })
    window.dispatchEvent(event)
  }

  /**
   * Dispatch accuracy update event
   */
  dispatchAccuracyEvent(accuracy) {
    const event = new CustomEvent('compassaccuracy', {
      detail: { accuracy }
    })
    window.dispatchEvent(event)
  }

  /**
   * Dispatch calibration state change event
   */
  dispatchCalibrationEvent(data) {
    const event = new CustomEvent('compasscalibration', {
      detail: data
    })
    window.dispatchEvent(event)
  }

  /**
   * Get calibration state
   */
  getCalibrationState() {
    return {
      isCalibrating: this.isCalibrating,
      progress: this.isCalibrating ? (this.calibrationHeadings.length / 50) * 100 : 0,
      accuracy: this.compassAccuracy
    }
  }

  /**
   * Get current heading without event listeners
   */
  getHeading() {
    return this.currentHeading
  }

  /**
   * Check if compass is available
   */
  hasCompass() {
    return this.isCompassAvailable
  }

  /**
   * Stop listening to device orientation
   */
  destroy() {
    if (!this.isListening) return

    const handler = this.handlers.get('deviceorientation')
    if (handler) {
      window.removeEventListener('deviceorientation', handler, true)
      this.handlers.delete('deviceorientation')
    }

    this.isListening = false
  }
}

// Singleton instance
let instance = null

/**
 * Get or create the device sensors manager
 */
export function getDeviceSensorsManager() {
  if (!instance) {
    instance = new DeviceSensorsManager()
  }
  return instance
}

/**
 * Initialize device sensors globally
 */
export async function initializeDeviceSensors() {
  const manager = getDeviceSensorsManager()
  return manager.init()
}

/**
 * Hook for React components to listen to device heading events
 */
export function useDeviceHeading(callback) {
  const manager = getDeviceSensorsManager()

  if (typeof window === 'undefined') return

  const handleHeading = (event) => {
    callback(event.detail.heading)
  }

  window.addEventListener('deviceheading', handleHeading)

  return () => {
    window.removeEventListener('deviceheading', handleHeading)
  }
}

/**
 * Hook for React components to listen to compass accuracy events
 */
export function useCompassAccuracy(callback) {
  if (typeof window === 'undefined') return

  const handleAccuracy = (event) => {
    callback(event.detail.accuracy)
  }

  window.addEventListener('compassaccuracy', handleAccuracy)

  return () => {
    window.removeEventListener('compassaccuracy', handleAccuracy)
  }
}

/**
 * Hook for React components to listen to calibration events
 */
export function useCompassCalibration(callback) {
  if (typeof window === 'undefined') return

  const handleCalibration = (event) => {
    callback(event.detail)
  }

  window.addEventListener('compasscalibration', handleCalibration)

  return () => {
    window.removeEventListener('compasscalibration', handleCalibration)
  }
}
